import {
  buildTutorialReplayMeta,
  buildTutorialReplayRecord,
  buildTutorialRunLeftRecord,
  TUTORIAL_ENGINE_ACTION_TYPES
} from '../../utils/tutorial/replay/tutorialReplayRecordRules';
import {getTutorialChapter} from '../../configs/tutorial';

const selectTutorialProgress = (state) => {
  const tutorialState = state?.private?.tutorial;
  if (!tutorialState?.chapterId) {
    return null;
  }
  return {
    stepIndex: tutorialState.stepIndex,
    activeModalId: tutorialState.activeModalId ?? null,
    isChapterCompleted: Boolean(tutorialState.isChapterCompleted),
  };
};

/**
 * Запись прохождения обучения (см. createTutorialReplayRecorder и правила
 * tutorialReplayRecordRules).
 *
 * Стоит там же, где сетевая партия отдает действия серверу, — перед
 * networkMiddleware: сюда доходит ровно то, что пропустил гейт обучения, в
 * том числе вложенные диспатчи остальных middleware (они заходят в цепочку
 * заново сверху). Место в записи резервируется до применения экшена, а
 * заполняется после — когда уже известно, изменил ли он общее состояние.
 * Заполняется и тогда, когда внутри диспатча что-то бросило исключение
 * (редьюсер или подписчик): пустое место остановило бы отправку всей
 * оставшейся записи, а уже примененное изменение потерялось бы.
 *
 * RESET_STATE — выход из главы (в меню, на следующую главу): запись
 * заканчивается меткой ухода с шагом, на котором игрок был, а сам сброс в
 * запись не идет — иначе просмотр обнулял бы поле на последнем кадре.
 */
export const createTutorialReplayMiddleware = ({getRecorder, now = () => Date.now()}) => (store) => (next) => (action) => {
  const recorder = getRecorder();
  if (!recorder || !action || !recorder.isRecording()) {
    return next(action);
  }

  if (action.type === 'RESET_STATE') {
    const stateBefore = store.getState();
    const tutorialState = stateBefore?.private?.tutorial;
    // Уход после пройденной главы — не «бросил»: метка только для недоигранной
    const finalRecord = tutorialState?.chapterId && !tutorialState.isChapterCompleted
      ? buildTutorialRunLeftRecord({
        stepIndex: tutorialState.stepIndex,
        stepId: getTutorialChapter(tutorialState.chapterId)?.steps?.[tutorialState.stepIndex]?.id ?? null,
        recordedAt: now(),
      })
      : null;
    recorder.setProgress(selectTutorialProgress(stateBefore));
    recorder.endRun(finalRecord);
    return next(action);
  }

  const slot = recorder.reserveSlot();
  const recordedAt = now();
  const sharedBefore = store.getState()?.shared;
  try {
    return next(action);
  } finally {
    const stateAfter = store.getState();
    const tutorialState = stateAfter?.private?.tutorial;
    recorder.fillSlot(slot, buildTutorialReplayRecord(action, {
      isSharedStateChanged: stateAfter?.shared !== sharedBefore,
      recordedAt,
      tutorialMeta: TUTORIAL_ENGINE_ACTION_TYPES.has(action.type)
        ? buildTutorialReplayMeta(tutorialState, getTutorialChapter(tutorialState?.chapterId))
        : null,
    }));
    recorder.setProgress(selectTutorialProgress(stateAfter));
  }
};
